import React, { useState, useEffect } from "react";
import api from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Gear, Gauge, Truck, CurrencyInr } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SettingsPage() {
  const [sizes, setSizes] = useState([]);
  const [pressure, setPressure] = useState({});
  const [cost, setCost] = useState({});
  const [mmSizes, setMmSizes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setSizes(data.tyre_sizes || []));
    api.get("/settings").then(({ data }) => {
      setPressure(data.recommended_pressure || {});
      setCost(data.cost_per_size || {});
      setMmSizes(data.make_model_sizes || []);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const cleanP = {};
      Object.entries(pressure).forEach(([k, v]) => { if (v !== "" && v != null) cleanP[k] = Number(v); });
      const cleanC = {};
      Object.entries(cost).forEach(([k, v]) => { if (v !== "" && v != null) cleanC[k] = Number(v); });
      await api.put("/settings", { recommended_pressure: cleanP, cost_per_size: cleanC, make_model_sizes: mmSizes });
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleMM = (s) => setMmSizes((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  return (
    <div className="max-w-3xl space-y-8" data-testid="settings-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          <Gear size={30} weight="fill" className="text-amber-500" /> Settings
        </h1>
        <p className="mt-1 text-sm text-zinc-400">Admin configuration for tyre sizes. Changes apply to all technicians.</p>
      </div>

      <section className="rounded-md border border-zinc-800 bg-zinc-900 p-5 ring-1 ring-white/5">
        <h2 className="mb-1 flex items-center gap-2 font-semibold text-zinc-100"><Gauge size={18} className="text-sky-400" /> Recommended Tyre Pressure (PSI) per size</h2>
        <p className="mb-4 text-xs text-zinc-500">Shown automatically during inspection so technicians can compare against actual pressure.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {sizes.map((s) => (
            <div key={s} className="flex items-center gap-3">
              <span className="w-24 font-mono text-sm text-zinc-300">{s}</span>
              <Input data-testid={`pressure-${s}`} type="number" placeholder="PSI" value={pressure[s] ?? ""}
                onChange={(e) => setPressure({ ...pressure, [s]: e.target.value })} className="bg-zinc-950" />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-md border border-zinc-800 bg-zinc-900 p-5 ring-1 ring-white/5">
        <h2 className="mb-1 flex items-center gap-2 font-semibold text-zinc-100"><CurrencyInr size={18} className="text-amber-400" /> Default Tyre Cost (₹) per size</h2>
        <p className="mb-4 text-xs text-zinc-500">Used to auto-set a new tyre's cost at fitment (editable per tyre). Drives Cost-per-KM (CPKM).</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {sizes.map((s) => (
            <div key={s} className="flex items-center gap-3">
              <span className="w-24 font-mono text-sm text-zinc-300">{s}</span>
              <Input data-testid={`cost-${s}`} type="number" placeholder="₹" value={cost[s] ?? ""}
                onChange={(e) => setCost({ ...cost, [s]: e.target.value })} className="bg-zinc-950" />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-md border border-zinc-800 bg-zinc-900 p-5 ring-1 ring-white/5">
        <h2 className="mb-1 flex items-center gap-2 font-semibold text-zinc-100"><Truck size={18} className="text-emerald-400" /> Sizes that require Vehicle Make & Model</h2>
        <p className="mb-4 text-xs text-zinc-500">For these sizes, technicians enter Make & Model when fitting to a new vehicle.</p>
        <div className="flex flex-wrap gap-2">
          {sizes.map((s) => (
            <button key={s} data-testid={`mm-${s}`} onClick={() => toggleMM(s)}
              className={`rounded-sm px-3 py-1.5 font-mono text-sm transition-colors ${mmSizes.includes(s) ? "bg-emerald-500 text-zinc-950" : "border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100"}`}>
              {s}
            </button>
          ))}
        </div>
      </section>

      <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
        {saving ? "Saving…" : "Save Settings"}
      </Button>
    </div>
  );
}
